import React, { forwardRef, useEffect } from "react";
import { useSelector } from "react-redux";

import { createSelector } from "@reduxjs/toolkit";

import { LokasiIcon, LookupIcon } from "@/assets/icons";
import { Markdown } from "@/components/ui/markdown";
import { useAnalysisSummaryDataContext } from "@/contexts/AnalysisSummaryDataContext";
import { useMap } from "@/hooks/map/core/useMapContext";
import { RootState } from "@/store";

import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";

import { useMarkdownSummary } from "../analysisSummaryWrapper/useMarkdownSummary";
import { MAX_GRIDS_SELECTED } from "../filterByGrid";
import GeneralSummary from "../overview/GeneralSummary";

const PrintHeader = ({
  currentPage,
  totalPage
}: {
  currentPage: number;
  totalPage: number;
}) => {
  return (
    <div className="bg-green-50 py-5 px-6 flex flex-row items-center mb-3 col-span-2">
      <div className="flex flex-row items-center justify-between gap-3 w-full">
        <div className="flex flex-col">
          <h1 className="text-teal-500 font-bold text-4xl">Analysis Result</h1>
          <p className="text-dim-gray font-semibold text-xs">
            {dayjs().format("DD MMMM, YYYY")}
          </p>
        </div>

        <LokasiIcon className="w-[126px] ml-auto" title="Lokasi" />
        <div>
          {currentPage} / {totalPage}
        </div>
      </div>
    </div>
  );
};

interface PrintAnalysisSummaryProps {
  chartImageData: string | null;
  dynamicChartImageData: string[];
  mapImage: string | null;
  executiveSummary?: string;
  isExpanded: boolean;
  onCollapse: () => void;
}

const PrintAnalysisSummary = forwardRef<
  HTMLDivElement,
  PrintAnalysisSummaryProps
>(
  (
    {
      chartImageData,
      dynamicChartImageData,
      mapImage,
      isExpanded,
      onCollapse,
      executiveSummary
    },
    ref
  ) => {
    const { map } = useMap();

    const { isCatchment, gridValue, isAllSelected, isSiteProfiling } =
      useAnalysisSummaryDataContext();

    const { generalSummary, gridsSelected, selectedAttribute } = useSelector(
      (state: RootState) => state.analysisSummary
    );

    const { replace } = useMarkdownSummary({ isExpanded, onCollapse });

    const markers = useSelector(
      createSelector(
        (state: RootState) =>
          state.spatial?.catchmentSettings?.placeCoordinates,
        (placeCoordinates) => (placeCoordinates ? [...placeCoordinates] : [])
      )
    );

    const dynamicChartPages = Math.max(
      1,
      Math.ceil(dynamicChartImageData.length / 2)
    );
    const executiveSummaryPageCount = executiveSummary ? 1 : 0;
    const totalPage = 1 + dynamicChartPages + executiveSummaryPageCount;

    useEffect(() => {
      if (!map) return;

      const addMarkerSymbols = () => {
        // Only add markers if isCatchment is true
        if (!isCatchment) return;

        const iconName = "custom-marker";
        const imageUrl = `${import.meta.env.VITE_BASE_PATH || ""}/icons/marker.svg`;

        const image = new Image();
        image.onload = () => {
          if (!map.hasImage(iconName)) {
            map.addImage(iconName, image);
          }
          addMarkerLayer(iconName);
        };
        image.onerror = (error) => {
          console.error(
            "Error loading custom marker image, falling back to default:",
            error
          );
          addMarkerLayer("marker-15");
        };
        image.src = imageUrl;
      };

      const addMarkerLayer = (iconImage: string) => {
        // Add a source for marker symbols
        if (!map.getSource("print-markers")) {
          map.addSource("print-markers", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: markers.map((marker, index) => ({
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: [marker.lng, marker.lat]
                },
                properties: {
                  id: marker.id || `marker-${index}`,
                  name: marker.locationName || `Marker ${index + 1}`
                }
              }))
            }
          });

          // Add symbol layer for markers
          map.addLayer({
            id: "print-marker-symbols",
            type: "symbol",
            source: "print-markers",
            layout: {
              "icon-image": iconImage,
              "icon-size": 1,
              "icon-offset": [0, -20],
              "icon-allow-overlap": true,
              "icon-ignore-placement": true
            },
            paint: {
              "text-color": "#000000",
              "text-halo-color": "#ffffff",
              "text-halo-width": 2,
              "icon-color": "#ef4444"
            }
          });
          map.moveLayer("print-marker-symbols");
        }
      };

      const removeMarkerSymbols = () => {
        if (map.getLayer("print-marker-symbols")) {
          map.removeLayer("print-marker-symbols");
        }
        if (map.getSource("print-markers")) {
          map.removeSource("print-markers");
        }
      };

      // Wait for map to be fully loaded and all layers rendered
      const captureMap = () => {
        map.once("idle", () => {
          try {
            addMarkerSymbols();
            // Single capture without multiple timeouts to prevent blinking
          } catch (error) {
            console.error("Error adding marker symbols:", error);
          }
        });
      };

      // Start the capture process
      captureMap();

      // Fallback timeout in case idle event doesn't fire
      const timeoutId = setTimeout(() => {
        try {
          // Add marker symbols even for fallback (only if isCatchment)
          addMarkerSymbols();
        } catch (error) {
          console.error("Error capturing map image:", error);
        }
      }, 3000);

      return () => {
        removeMarkerSymbols();
        clearTimeout(timeoutId);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, isCatchment]);

    return (
      <div ref={ref} className="h-screen w-screen p-4">
        <PrintHeader currentPage={1} totalPage={totalPage} />

        <img
          src={mapImage ?? ""}
          alt="Map snapshot"
          className="w-full max-h-[500px] object-cover"
        />
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="flex flex-col gap-4">
            <GeneralSummary />

            <div
              className="mt-2 mb-4 flex flex-row items-center justify-between bg-white py-2 px-3 border border-gainsboro rounded-xl"
              role="region"
              aria-label={`Total ${isCatchment ? "Catchment" : "Grid"} Summary`}
            >
              <p>Total {isCatchment ? "Catchment" : "Grid"}</p>
              <p data-testid="total-grid-label">
                {generalSummary?.data.general.total_rows}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <h1
              className="text-txtHeading text-sm font-semibold"
              data-testid={`top-10-${isCatchment ? "catchment" : "grid"}-by-attribute`}
            >
              Top 10 {isCatchment ? "Catchment" : "Grid"} by{" "}
              <span className="capitalize">{selectedAttribute.label}</span>
            </h1>

            <img
              src={chartImageData ?? ""}
              alt={`Chart ${selectedAttribute.label}`}
              className="w-full object-contain"
            />
          </div>
        </div>

        <div className="new-page-break" />

        <PrintHeader currentPage={2} totalPage={totalPage} />

        <div className="mt-4 flex flex-row items-center justify-between">
          <div>
            <span className="text-base font-semibold text-txtHeading">
              Filter by Analysis Area
            </span>
          </div>

          <div
            className="flex flex-row items-center gap-3 px-2 py-3 bg-teal-50 border border-teal-500 rounded-xl min-w-[170px] max-w-xl"
            data-testid="filter-by-analysis-area"
          >
            <div className="flex gap-2 mr-8">
              <LookupIcon className="fill-teal-500 w-5 h-5" />
              <span className="text-xs font-semibold">Score</span>
            </div>
            {isAllSelected ? (
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-teal-500">
                  {isSiteProfiling ? "CatchmentID" : "GridID"}: All
                </span>
                <span className="text-xs font-semibold text-teal-500">N/A</span>
              </div>
            ) : (
              <div
                className={twMerge(
                  "flex gap-3",
                  gridsSelected.length === MAX_GRIDS_SELECTED &&
                    "justify-between"
                )}
              >
                {gridsSelected.map((grid) => {
                  const gridData = gridValue.find(
                    (item) => item.value === grid
                  );
                  if (!gridData) return null;
                  return (
                    <div
                      key={gridData.value}
                      className={twMerge(
                        "flex flex-col",
                        gridsSelected.length === 2 && "w-1/2"
                      )}
                    >
                      <span className="text-xs font-semibold text-teal-500">
                        {isSiteProfiling ? "CatchmentID" : "GridID"}:{" "}
                        {gridData.value}
                      </span>
                      <span className="text-xs font-semibold text-teal-500">
                        {(gridData.score ?? 0).toLocaleString()}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          {dynamicChartImageData.map((image, index) => (
            <React.Fragment key={index}>
              {index % 2 === 0 && index !== 0 && (
                <>
                  <div className="new-page-break col-span-2" />
                  <PrintHeader
                    currentPage={index / 2 + 2}
                    totalPage={totalPage}
                  />
                </>
              )}

              <img
                src={image}
                alt="Chart"
                className="w-full object-contain"
                key={index}
              />
            </React.Fragment>
          ))}
        </div>

        {executiveSummary && (
          <>
            <div className="new-page-break" />
            <PrintHeader currentPage={totalPage} totalPage={totalPage} />
            <div className="markdown-ai-summary border border-whisper rounded-lg p-4">
              <Markdown content={executiveSummary} replace={replace} />
            </div>
          </>
        )}
      </div>
    );
  }
);

export default PrintAnalysisSummary;
